import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'

const MOCK_SUMMARY = {
  onlineDevices: '8 / 10',
  openAlerts: 2,
  avgUptime: '99.2%',
}

const MOCK_SITES = [
  {
    id: 'site-001',
    nameKey: 'assets.sites.alpha',
    planKey: 'assets.plans.pro',
    devices: '1 / 1',
    hashrate: '120 TH/s',
    lastSync: '12s',
    uptime: 99,
    status: 'online',
    color: '#1A56FF',
    gradStart: '#1A56FF',
    gradEnd: '#00D4FF',
    bg: 'rgba(26, 86, 255, 0.07)',
  },
  {
    id: 'site-002',
    nameKey: 'assets.sites.delta',
    planKey: 'assets.plans.core',
    devices: '3 / 4',
    hashrate: '360 TH/s',
    lastSync: '48s',
    uptime: 92,
    status: 'warning',
    color: '#F7931A',
    gradStart: '#F7931A',
    gradEnd: '#FFD060',
    bg: 'rgba(247, 147, 26, 0.08)',
  },
  {
    id: 'site-003',
    nameKey: 'assets.sites.north',
    planKey: 'assets.plans.enterprise',
    devices: '4 / 5',
    hashrate: '500 TH/s',
    lastSync: '8s',
    uptime: 100,
    status: 'online',
    color: '#00C896',
    gradStart: '#00C896',
    gradEnd: '#6FE7C7',
    bg: 'rgba(0, 200, 150, 0.08)',
  },
]

function ProgressBar({ value, color }: { value: number; color: string }) {
  return (
    <div
      style={{
        height: '4px',
        background: 'rgba(0,0,0,0.06)',
        borderRadius: '99px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${Math.min(100, value)}%`,
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: '99px',
          transition: 'width 0.6s ease',
        }}
      />
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const toneMap = {
    online: { bg: 'rgba(0, 200, 150, 0.1)', border: 'rgba(0, 200, 150, 0.2)', text: '#00C896', label: t('assets.statusOnline') },
    warning: { bg: 'rgba(247, 147, 26, 0.1)', border: 'rgba(247, 147, 26, 0.2)', text: '#F7931A', label: t('assets.statusWarning') },
    offline: { bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.2)', text: '#9CA3AF', label: t('assets.statusOffline') },
  } as const

  const tone = toneMap[status as keyof typeof toneMap] ?? toneMap.offline

  return (
    <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1" style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
      <div className="h-1.5 w-1.5 rounded-full" style={{ background: tone.text, boxShadow: `0 0 6px ${tone.text}` }} />
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', fontWeight: 600, color: tone.text }}>{tone.label}</span>
    </div>
  )
}

export function AssetsPage() {
  const { t } = useTranslation()
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { replace: true })
  }, [isAuthenticated, navigate])

  if (!isAuthenticated) return null

  return (
    <div className="flex min-h-screen flex-col" style={{ background: '#F0F4FF' }}>
      <main className="flex-1" style={{ paddingTop: '80px', paddingBottom: '60px' }}>
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 800,
                  fontSize: 'clamp(24px, 3vw, 32px)',
                  color: '#0D1B3E',
                  letterSpacing: '-0.03em',
                  marginBottom: '4px',
                }}
              >
                {t('assets.title')}
              </h1>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#6B7A99' }}>
                {t('assets.welcome')}{' '}
                <span style={{ color: '#1A56FF', fontWeight: 600 }}>{user?.name || t('assets.guestName')}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="flex items-center gap-2 rounded-xl px-4 py-2 transition-all duration-200"
              style={{
                background: 'rgba(26, 86, 255, 0.06)',
                border: '1px solid rgba(26, 86, 255, 0.12)',
                color: '#6B7A99',
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '13px',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.06)'
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)'
                e.currentTarget.style.color = '#EF4444'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(26, 86, 255, 0.06)'
                e.currentTarget.style.borderColor = 'rgba(26, 86, 255, 0.12)'
                e.currentTarget.style.color = '#6B7A99'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('assets.signOut')}
            </button>
          </div>

          <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            <div
              className="relative overflow-hidden rounded-3xl p-6"
              style={{
                background: 'linear-gradient(135deg, #0D1B3E 0%, #1A2E5A 100%)',
                boxShadow: '0 8px 32px rgba(13, 27, 62, 0.25)',
              }}
            >
              <div
                className="absolute top-0 right-0 h-40 w-40 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(26,86,255,0.2) 0%, transparent 70%)',
                  transform: 'translate(30%, -30%)',
                }}
              />
              <div className="relative">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect x="4" y="5" width="16" height="14" rx="3" stroke="white" strokeWidth="1.8" />
                      <path d="M8 10h8M8 14h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                    {t('assets.onlineDevices')}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: 'Space Grotesk, sans-serif',
                    fontWeight: 700,
                    fontSize: 'clamp(24px, 3vw, 32px)',
                    color: 'white',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                    marginBottom: '8px',
                  }}
                >
                  {MOCK_SUMMARY.onlineDevices}
                </div>
                <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{t('assets.onlineDevicesCaption')}</div>
              </div>
            </div>

            <div className="rounded-3xl border border-[#1A56FF]/10 bg-white p-6" style={{ boxShadow: '0 4px 24px rgba(26, 86, 255, 0.07)' }}>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: 'rgba(247, 147, 26, 0.1)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M12 4a6 6 0 016 6v3.2l1.6 2.1a1 1 0 01-.8 1.6H5.2a1 1 0 01-.8-1.6L6 13.2V10a6 6 0 016-6z"
                      stroke="#F7931A"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                    <path d="M10 20a2 2 0 004 0" stroke="#F7931A" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#6B7A99', fontWeight: 500 }}>{t('assets.openAlerts')}</span>
              </div>
              <div
                style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(20px, 2.5vw, 26px)',
                  color: '#F7931A',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  marginBottom: '6px',
                }}
              >
                {MOCK_SUMMARY.openAlerts}
              </div>
              <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.alertSummary')}</div>
            </div>

            <div className="rounded-3xl border border-[#1A56FF]/10 bg-white p-6" style={{ boxShadow: '0 4px 24px rgba(26, 86, 255, 0.07)' }}>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: 'rgba(0, 200, 150, 0.1)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <polyline points="4 14 9 9 13 13 20 6" stroke="#00C896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="14 6 20 6 20 12" stroke="#00C896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#6B7A99', fontWeight: 500 }}>{t('assets.avgUptime')}</span>
              </div>
              <div
                style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(20px, 2.5vw, 26px)',
                  color: '#0D1B3E',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  marginBottom: '6px',
                }}
              >
                {MOCK_SUMMARY.avgUptime}
              </div>
              <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.uptimeSummary')}</div>
            </div>
          </div>

          <div>
            <div className="mb-5 flex items-center justify-between">
              <h2
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#0D1B3E',
                  letterSpacing: '-0.02em',
                }}
              >
                {t('assets.managedSites')}
              </h2>
              <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: 'rgba(26, 86, 255, 0.07)', border: '1px solid rgba(26, 86, 255, 0.12)' }}>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1A56FF' }}>
                  {t('assets.activeCount', { count: MOCK_SITES.filter((site) => site.status === 'online').length })}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {MOCK_SITES.map((site) => (
                <div
                  key={site.id}
                  className="relative overflow-hidden rounded-3xl border border-[#1A56FF]/10 bg-white p-6 transition-all duration-300"
                  style={{ boxShadow: '0 4px 24px rgba(26, 86, 255, 0.06)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = `0 12px 40px ${site.color}18`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 24px rgba(26, 86, 255, 0.06)'
                  }}
                >
                  <div className="absolute top-0 bottom-0 left-0 w-1 rounded-l-3xl" style={{ background: `linear-gradient(180deg, ${site.gradStart}, ${site.gradEnd})` }} />
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <div className="mb-2 inline-flex items-center rounded-full px-2.5 py-1" style={{ background: site.bg, border: `1px solid ${site.color}25` }}>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, color: site.color }}>{t(site.planKey)}</span>
                      </div>
                      <h3
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          fontSize: '17px',
                          color: '#0D1B3E',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {t(site.nameKey)}
                      </h3>
                    </div>
                    <StatusBadge status={site.status} />
                  </div>

                  <div className="mb-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.siteDevices')}</span>
                      <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '13px', fontWeight: 700, color: site.color }}>{site.devices}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.siteHashrate')}</span>
                      <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '13px', fontWeight: 700, color: '#0D1B3E' }}>{site.hashrate}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.lastSync')}</span>
                      <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '13px', fontWeight: 600, color: '#00C896' }}>
                        {t('assets.secondsAgo', { value: site.lastSync })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE' }}>{t('assets.subscription')}</span>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 600, color: site.color }}>{t(site.planKey)}</span>
                    </div>
                  </div>

                  <div className="pt-4" style={{ borderTop: `1px solid ${site.color}10` }}>
                    <div className="mb-2 flex items-center justify-between">
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', color: '#9BA8BE' }}>{t('assets.uptime')}</span>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', fontWeight: 600, color: site.color }}>{site.uptime}%</span>
                    </div>
                    <ProgressBar value={site.uptime} color={site.color} />
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-5 flex items-center gap-4 rounded-2xl p-5"
              style={{
                background: 'rgba(26, 86, 255, 0.04)',
                border: '1.5px dashed rgba(26, 86, 255, 0.15)',
              }}
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl" style={{ background: 'rgba(26, 86, 255, 0.08)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="10" stroke="#1A56FF" strokeWidth="1.8" />
                  <path d="M12 8v4M12 16h.01" stroke="#1A56FF" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#6B7A99', lineHeight: 1.6 }}>
                {t('assets.demoNoteBefore')}
                <strong className="text-[#1A56FF]">{t('assets.demoNoteStrong')}</strong>
                {t('assets.demoNoteAfter')}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
